import axios, { AxiosError } from 'axios';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useState, useRef, useEffect,
  useCallback, useMemo,
} from 'react';
import Dropzone from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Row, Col, Accordion, AccordionBody, AccordionHeader,
  AccordionItem, Progress,
} from 'reactstrap';

import {
  markAsUrgentDispatch,
} from '@reducers/referral/ReferralAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { uploadFileArr } from '@services/UploadService';
import { CustomButton } from '@shared/buttons';
import Alert from '@shared/components/alert/Alert';
import { formatBytes } from '@shared/helpers';
import useFormPrompt from '@shared/hooks/useFormPrompt';
import {
  SavedIcon, CloudUploadIcon, FileIcon,
  AttachmentCloseIcon, AttachmentRetryIcon,
  AttachmentTrashIcon,
} from '@shared/icons';

import { PendingSaveResult } from './ReferralInformationForm';
import { useGuestReferral, useReferralStepRequests } from '../guest/GuestReferralContext';

import '../SendReferralOrderPageStyle.scss';

type AttachmentFormProps = {
  open: string;
  toggle: (e: any) => void;
  // loadingDetails: boolean;
  persistPendingReferralInfo?: (latestReferral: any) => Promise<PendingSaveResult>;
};

const AttachmentForm = ({
  open, toggle, persistPendingReferralInfo,
}: AttachmentFormProps) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const requests = useReferralStepRequests();
  const guest = useGuestReferral();
  const returnTo = location?.state?.returnTo;
  const i18n = LocaleService.getTranslations('referrals.sendReferral');
  const i18nAttachments = LocaleService.getTranslations('referrals.sendReferral.forms.attachments');
  const referralDetails = useSelector(({ Referral }) => Referral.referralData);
  const isUrgent = useSelector(({ Referral }) => Referral.isUrgent);
  const isUpdate = location?.pathname?.includes('view') && referralDetails?.status !== 'draft';
  const isCompleted = referralDetails?.status === 'completed';
  const sendLabel = guest?.submitLabel ?? (isUpdate ? i18n.button.update : i18n.button.send);
  const [ saved, setSaved ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ invalid, setInvalid ] = useState(false);
  const [ errors, setErrors ] = useState<any>([]);
  const [ onDropZone, setOnDropZone ] = useState(false);
  const [ isDisabled, setIsDisabled ] = useState(false);
  const [ uploadedAttachments, setUploadedAttachments ] = useState<any>([]);
  const [ initialAttachments, setInitialAttachments ] = useState<any>([]);
  const [ isSaving, setIsSaving ] = useState(false);
  const attachmentProgressRef = useRef<any>([]);
  const initialData = useMemo(() => {
    if (open !== '5') return {}; // Return empty object if accordion is closed

    return {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      files: initialAttachments ?? [],
      is_urgent: referralDetails?.is_urgent || false,
    };
  }, [ open, referralDetails, initialAttachments ]);

  const isDirty = useCallback(() => {
    // Skip the check if accordion is closed
    if (open !== '5' || saved) return false;
    const newData = {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      files: uploadedAttachments ?? [],
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };

    return !isEqual(newData, initialData);
  }, [
    initialData, uploadedAttachments,
    referralDetails, open, saved, isUrgent,
  ]);

  const updateAttachmentProgress = (fileName: string, loaded: any, total: number) => {
    attachmentProgressRef.current[fileName] = {
      ...attachmentProgressRef.current[fileName],
      loaded: formatBytes(loaded),
      progress: Math.round((loaded / total) * 100),
    };

    // Trigger a state update to force re-render
    setUploadedAttachments((prevAttachments: any) => [ ...prevAttachments ]);
  };

  const handleSaveAsDraft = async () => {
    // A recipient still waiting in step 1 belongs in the draft too.
    const pending = (await persistPendingReferralInfo?.(referralDetails)) ?? 'none';
    if (pending === 'failed') return;
    setIsSaving(true);
    try {
      const newData = {
        referral_id: referralDetails?.id,
        patient_id: referralDetails?.patient?.id,
        files: uploadedAttachments,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
        save_as_draft: true,
      };

      await dispatch(requests.saveReferralAttachmentRequest(newData)).$promise;
      setSaved(true);
      // A guest's files stay in the form (ENH-052); there is no draft to leave for.
      if (guest) return;
      navigate(returnTo);
    } catch (error) {
      toast.error(i18n.errors.draftSaveFailed);
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendReferral = async () => {
    if (guest && !guest.canSubmit) {
      guest.onSubmitBlocked();
      return;
    }
    if (!referralDetails?.patient) {
      toast.error(i18n.errors.patient);
      return;
    }
    if (!referralDetails.referred_to && !referralDetails.referred_to_clinic) {
      // Step 1 may only be waiting for the draft (reached without Patient
      // History's Next): save it now instead of rejecting the send.
      const pending = (await persistPendingReferralInfo?.(referralDetails)) ?? 'none';
      if (pending !== 'saved') {
        if (pending === 'none') toast.error(i18n.errors.referral);
        return;
      }
    }
    setLoading(true);
    setInvalid(false);

    try {
      const newData = {
        referral_id: referralDetails?.id,
        patient_id: referralDetails?.patient?.id,
        files: uploadedAttachments,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
      };

      const result = await dispatch(requests.saveReferralAttachmentRequest(newData)).$promise;
      setSaved(true);
      if (guest) {
        guest.onSubmitted(result?.data?.data);
        return;
      }

      setTimeout(() => {
        if (referralDetails?.status === 'draft') {
          navigate('/referrals/pending', { state: { filterValue: 'sent' } });
          return;
        }
        navigate('/referrals/all', { state: { tab: 'all' } });
      }, 300);
    } catch (error) {
      if (guest) {
        const fieldErrors = guest.onSubmitError(error);
        if (fieldErrors) setErrors(fieldErrors);
        return;
      }
      const { response: errorResponse } = error as AxiosError;
      if (errorResponse?.status === 422) {
        const { errors: errorData } = errorResponse.data;
        setErrors(errorData);
      } else {
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSaveExit = async (e?: any) => {
    e?.stopPropagation();
    setIsSaving(true);
    setInvalid(false);
    // message will prompt that the user should
    // fill out step 4 first
    if (!referralDetails?.services || referralDetails?.services?.length === 0) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    try {
      const newData = {
        referral_id: referralDetails?.id,
        patient_id: referralDetails?.patient?.id,
        files: uploadedAttachments,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
        save_as_draft: true,
      };

      await dispatch(requests.saveReferralAttachmentRequest(newData)).$promise;
      setSaved(true);
      navigate(returnTo);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          setLoading(false);
          return;
        }
        handleError(error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDiscardChanges = () => {
    dispatch(markAsUrgentDispatch(referralDetails?.is_urgent || false));

    if (!referralDetails?.attachments || referralDetails?.attachments?.length === 0 || open !== '5') return;
    const updatedAttachments = referralDetails.attachments.map((attachment: any) => ({
      ...attachment,
      loaded: 'success',
      loading: false,
      total_loaded: 100,
      loaded_value: attachment?.file_size,
      type_icon: <FileIcon />,
    }));
    setUploadedAttachments(updatedAttachments);
    setInitialAttachments(updatedAttachments);
  };

  const { confirm } = useFormPrompt({
    onSave: async () => {
      // Save form logic
      await handleSaveAsDraft();
    },
    onDiscard: () => {
      // Discard changes logic
      onDiscardChanges();
    },
    // isFormDirty: isDirty,
    isFormDirty: isDirty(),
  });

  const toggleAccordion = async () => {
    // Call confirm prompt before closing accordion
    // if there are any changes
    if (open === '5' && isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    toggle('5');
  };

  const onHandleBack = async () => {
    if (isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    // A guest (ENH-052) has no page to return to: Back is the previous step.
    if (guest) {
      toggle('4');
      return;
    }
    navigate(returnTo);
  };

  const onResponse = (res: any, data: any) => {
    // if there is an error in the generating the s3 signed url, status returns 400
    if (res?.status === 400) {
      setUploadedAttachments((state: any) => {
        const updatedAttachments = [ ...state ];
        const index = state.findIndex((a: any) => a.filename === data.originalFilename);
        // add validation to avoid errors if index is less than 0
        if (index >= 0) {
          updatedAttachments[index].loading = false;
          updatedAttachments[index].loaded = data.response ? data.response : 'error';
          updatedAttachments[index].error_message = data.message
            ? data.message : i18nAttachments.error.message;
        }
        return updatedAttachments;
      });
      return;
    }

    // if there are no issues in uploading in s3 bucket, status returns 200
    if (res?.status === 200) {
      setUploadedAttachments((state: any) => {
        const updatedAttachments = [ ...state ];
        const index = state.findIndex((a: any) => a.filename === data.originalFilename);
        // add validation to avoid errors if index is less than 0
        if (index >= 0) {
          updatedAttachments[index].file_object = data?.file;
          updatedAttachments[index].file = data?.filename;
          updatedAttachments[index].filename = data?.originalFilename;
          updatedAttachments[index].file_size = `${data?.file.size}`;
          updatedAttachments[index].loading = false;
          updatedAttachments[index].loaded = 'success';
          updatedAttachments[index].url = data?.url;
        }
        return updatedAttachments;
      });
      return;
    }

    // if there is an issue in uploading in s3 bucket, returns failed
    setUploadedAttachments((state: any) => {
      const updatedAttachments = [ ...state ];
      const index = state.findIndex((a: any) => a.filename === data.originalFilename);
      // add validation to avoid errors if index is less than 0
      if (index >= 0) {
        updatedAttachments[index].loaded = 'error';
        updatedAttachments[index].loading = false;
        updatedAttachments[index].error_message = i18nAttachments.error.message;
      }
      return updatedAttachments;
    });
  };

  const onDragEnter = () => setOnDropZone(true);

  const onDragLeave = () => setOnDropZone(false);

  const onDropItem = async (acceptedFiles: any) => {
    // disable dropzone border after dropping the item
    onDragLeave();
    if (!acceptedFiles.length) return;

    const newAttachments = acceptedFiles.map((d: any) => {
      const newData = {
        file: d,
        filename: d.name,
        file_size: `${d.size}`,
        url: d.url,
        loaded: 'pending',
        error_message: '',
        loading: true,
        type: d.type,
        type_icon: <FileIcon />,
      };
      return newData;
    });

    setUploadedAttachments([ ...uploadedAttachments, ...newAttachments ]);

    if (acceptedFiles.length === 0) return;

    try {
      let data: any;
      setIsDisabled(true);
      // upload file to s3 temp
      await uploadFileArr('referral_attachments', acceptedFiles)
        .then((response) => {
          data = response;
        })
        .catch((err) => {
          setUploadedAttachments([]);
          handleError(err);
        })
        .finally(() => {
          data.forEach((fileData: any) => {
            if (!fileData.url) {
              const res = { status: 400 };
              onResponse(res, fileData);
              return;
            }

            axios.put(fileData.url, fileData.file, {
              onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                updateAttachmentProgress(fileData.originalFilename, loaded, total);
              },
            })
              .then((response) => {
                onResponse(response, fileData);
              })
              .catch((error) => {
                onResponse(error.response, fileData);
              });
          });
          setIsDisabled(false);
        });
    } catch (error) {
      // setIsDisabled(false);
      setUploadedAttachments([]);
      handleError(error);
    } finally {
      setIsDisabled(false);
    }
  };

  const deleteFile = (filepath: any) => {
    // Filter out the file to be deleted
    const updatedFiles = uploadedAttachments.filter((file: any) => file.filename !== filepath);
    setUploadedAttachments(updatedFiles);

    // Remove progress entry
    attachmentProgressRef.current = Object.keys(attachmentProgressRef.current)
      .reduce((acc: any, key) => {
        if (key !== filepath) {
          acc[key] = attachmentProgressRef.current[key];
        }
        return acc;
      }, {});
  };

  const onRetryUpload = async (data: any) => {
    if (!data) return;
    setIsDisabled(true);
    const acceptedFiles = [];
    acceptedFiles.push(data.file);
    setUploadedAttachments((state: any) => {
      const updatedAttachments = [ ...state ];
      const index = state.findIndex((a: any) => a.filename === data.filename);
      // add validation to avoid errors if index is less than 0
      if (index >= 0) {
        updatedAttachments[index].loaded = 'pending';
        updatedAttachments[index].error_message = '';
      }
      return updatedAttachments;
    });

    try {
      let resData: any;
      // upload file to s3 temp
      await uploadFileArr('referral_attachments', acceptedFiles)
        .then((response) => {
          resData = response;
        })
        .catch((err) => {
          setUploadedAttachments([]);
          handleError(err);
        })
        .finally(() => {
          resData.forEach((fileData: any) => {
            if (!fileData.url) {
              const res = { status: 400 };
              onResponse(res, fileData);
              return;
            }

            axios.put(fileData.url, fileData.file, {
              onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                updateAttachmentProgress(fileData.originalFilename, loaded, total);
              },
            })
              .then((response) => {
                onResponse(response, fileData);
              })
              .catch((error) => {
                onResponse(error.response, fileData);
              });
          });
          setIsDisabled(false);
        });
    } catch (error) {
      // setIsDisabled(false);
      setUploadedAttachments([]);
      handleError(error);
    } finally {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    const initiate = () => {
      if (!referralDetails?.attachments || referralDetails?.attachments?.length === 0 || open !== '5') return;
      const updatedAttachments = referralDetails.attachments.map((attachment: any) => ({
        ...attachment,
        loaded: 'success',
        loading: false,
        total_loaded: 100,
        loaded_value: attachment?.file_size,
        type_icon: <FileIcon />,
      }));
      setUploadedAttachments(updatedAttachments);
      setInitialAttachments(updatedAttachments);
    };

    initiate();

    // clear data when component unmounts
    return () => {
      // setUploadedAttachments([]);
      setInvalid(false);
    };
  }, [ referralDetails?.attachments, open ]);

  return (
    <div className="ref-attachment-container">
      <Accordion
        open={open}
        toggle={toggleAccordion}
        className={classNames('mb-3', {
          'sr-accordion-collapsed': open === '5',
        })}
      >
        <AccordionItem>
          <AccordionHeader className="sr-accordion-header" targetId="5">
            <Row className="align-items-center">
              <Col xs={3} md={1} style={{ width: '50px' }}>
                {
                  (saved || (referralDetails?.attachments && referralDetails?.attachments.length > 0)) && open !== '5' ? (<SavedIcon />) : (
                    <span
                      className={classNames('sr-accordion-number', {
                        'sr-accordion-number-collapsed': open === '5',
                      })}
                    >
                      5
                    </span>
                  )
                }
              </Col>
              <Col xs={9} md={7} lg={8} xl={9}>
                <span className="sr-accordion-title">
                  {i18nAttachments.title}
                </span>
              </Col>
              {!isCompleted && isUpdate && open === '5' && (
                <Col xs={12} md={4} lg={3} xl={2} className="sr-save-exit-container">
                  <CustomButton
                    id="btn-save-exit"
                    className="btn-lg sr-save-exit-btn"
                    color="primary"
                    onClick={(e: any) => onSaveExit(e)}
                    disabled={loading || isSaving}
                    loading={isSaving}
                  >
                    {i18n.button.saveExit}
                  </CustomButton>
                </Col>
              )}
            </Row>
          </AccordionHeader>
          <AccordionBody accordionId="5">
            {invalid && (
              <Alert color="error">{LocaleService.parseTranslation(i18n.invalid, {
                ':number': 4,
              })}
              </Alert>
            )}
            <Row>
              <Col xs={1} style={{ width: '50px' }} />
              <Col xs={12} lg={11}>
                <div
                  className={classNames('dropzone-container mb-4', {
                    'dropzone-container-error': errors.files && errors.files.length > 0,
                  })}
                >
                  <Dropzone
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDropItem}
                    disabled={isCompleted}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section>
                        <Row>
                          <Col>
                            <div
                              className={onDropZone ? 'position-block' : ''}
                              {...getRootProps()}
                            >
                              <input {...getInputProps()} />
                              <div className="placeholder-container">
                                <CloudUploadIcon />
                              </div>
                              <div className="placeholder-container hide-placeholder mb-2">
                                <span className="placeholder-label">
                                  {LocaleService.parseTranslationElement(
                                    i18nAttachments.placeholder.label, {
                                    // eslint-disable-next-line max-len
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                      browse: (label: string) => (
                                        <span className="placeholder-browse-style" style={{ cursor: 'pointer' }}>
                                          {label}
                                        </span>
                                      ),
                                    })}
                                </span>
                              </div>
                              <div className="placeholder-container hide-placeholder">
                                <span className="placeholder-description">
                                  {i18nAttachments.placeholder.format}
                                </span>
                              </div>
                              <div className="placeholder-container hide-placeholder">
                                <span className="placeholder-description">
                                  {i18nAttachments.placeholder.maxSize}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </section>
                    )}
                  </Dropzone>
                </div>
                <div className="mb-4">
                  {
                    uploadedAttachments && uploadedAttachments.length > 0
                      ? uploadedAttachments.map((file: any, idxFile: number) => (
                        <Row key={`${idxFile.toString()}`} className="mb-3">
                          <Col xs={12} lg={6} className="custom-col-style">
                            <div
                              className={classNames('file-preview-container', {
                                'file-preview-container-error': file.error_message,
                              })}
                            >
                              <Row>
                                <Col xs={3} sm={2} className="file-icon-container">
                                  <span className="file-icon">{file.type_icon}</span>
                                </Col>
                                <Col xs={9} sm={10}>
                                  <div className="file-name">
                                    {file.filename}
                                  </div>
                                  <div className="progress-row-container">
                                    <span style={{ flex: 1 }}>
                                      <Progress
                                        value={
                                          file.loaded === 'error' ? 100
                                            : (attachmentProgressRef
                                              .current[file.filename]?.progress
                                              || file.total_loaded || 0)
                                        }
                                        className={classNames('custom-progress-bar-style', {
                                          'custom-progress-success': file.loaded === 'success' && !file.loading,
                                          'custom-progress-error': file.loaded === 'error' && !file.loading,
                                        })}
                                      />
                                    </span>
                                    <span style={{ display: 'flex' }}>
                                      {file.loading && (
                                        <a
                                          className="custom-icon-style"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteFile(file.filename);
                                          }}
                                        >
                                          <span className="custom-close-icon-style">
                                            <AttachmentCloseIcon />
                                          </span>
                                        </a>
                                      )}
                                      {file.loaded === 'success' && !file.loading
                                        && !isCompleted && (
                                        <a
                                          className="custom-icon-style"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteFile(file.filename);
                                          }}
                                        >
                                          <AttachmentTrashIcon />
                                        </a>
                                      )}
                                      {file.loaded === 'error' && !file.loading && (
                                        <>
                                          <a
                                            className="custom-icon-style"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onRetryUpload(file);
                                            }}
                                          >
                                            <AttachmentRetryIcon />
                                          </a>
                                          <a
                                            className="custom-icon-style"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteFile(file.filename);
                                            }}
                                          >
                                            <AttachmentTrashIcon />
                                          </a>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                  {
                                    file.error_message
                                      ? (
                                        <span className="file-error-msg">
                                          {file.error_message}
                                        </span>
                                      )
                                      : (
                                        <div className="file-size">
                                          {file?.file_size && LocaleService.parseTranslation(
                                            i18nAttachments.fileSize, {
                                              ':loaded': attachmentProgressRef.current[file.filename]?.loaded || formatBytes(file.loaded_value) || 0,
                                              ':total': formatBytes(file.file_size),
                                            })}
                                        </div>
                                      )
                                  }
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>
                      )) : null
                  }
                </div>
                {!isCompleted && (
                  <div className="mb-4">
                    {/* <div className="mb-3 sr-info-style">
                      <InfoFilledIcon />
                      <span className="sr-info-style-text">{i18n.info}</span>
                    </div> */}
                    {guest?.submitExtras}
                    <div className="sr-footer-btn-container">
                      <CustomButton
                        id="btn-back"
                        className="btn-lg sr-back-btn"
                        color="secondary"
                        onClick={onHandleBack}
                        disabled={loading}
                      >
                        {i18n.button.back}
                      </CustomButton>
                      <CustomButton
                        id="btn-next"
                        className="btn-lg sr-next-btn"
                        color="primary"
                        onClick={handleSendReferral}
                        disabled={
                          loading || isDisabled
                          || uploadedAttachments.some((file: any) => file.loaded === 'error' || file.loading === true)
                        }
                        loading={loading}
                      >
                        {sendLabel}
                      </CustomButton>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default AttachmentForm;
